<template>
  <div class="last-samples-empty" role="status">
    <div class="last-samples-empty__illustration" aria-hidden="true">
      <img :src="emptyStateIconSvg" alt="" />
    </div>

    <div class="last-samples-empty__content">
      <h3 class="last-samples-empty__title">
        {{ $t('lastSamples.emptyState.title') }}
      </h3>
      <p class="last-samples-empty__description">
        {{ $t('lastSamples.emptyState.descriptionBeforePeriod') }}
        <strong>
          {{ $t('lastSamples.emptyState.descriptionPeriod') }}
        </strong>
        {{ $t('lastSamples.emptyState.descriptionAfterPeriod') }}
      </p>

      <div
        class="last-samples-empty__counters"
        :aria-label="$t('lastSamples.title')"
      >
        <div
          v-for="counter in counters"
          :key="counter.key"
          class="last-samples-empty__counter"
          :class="`last-samples-empty__counter--${counter.key}`"
        >
          <span class="last-samples-empty__counter-icon">
            <img :src="counter.icon" alt="" />
          </span>
          <span class="last-samples-empty__counter-number">0</span>
          <span class="last-samples-empty__counter-label">
            {{ counter.label }}
          </span>
        </div>
      </div>
    </div>

    <aside class="last-samples-empty__side">
      <div class="last-samples-empty__status-row">
        <div
          class="last-samples-empty__status-icon"
          aria-hidden="true"
        >
          <svg viewBox="0 0 24 24">
            <path d="M5 12.5 10 17.5 19 7.5" />
          </svg>
        </div>
        <p class="last-samples-empty__last-update">
          {{ $t('lastSamples.emptyState.lastUpdated') }}
          {{ formattedLastRefreshAt }}
        </p>
      </div>

      <button
        type="button"
        class="last-samples-empty__history-link"
        @click="navigateToSamplesHistory"
      >
        <span
          class="last-samples-empty__history-icon"
          aria-hidden="true"
        >
          <img :src="historyIconSvg" alt="" />
        </span>
        <span>{{ $t('lastSamples.emptyState.historyLink') }}</span>
        <svg
          class="last-samples-empty__history-arrow"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path d="M9 5 16 12 9 19" />
        </svg>
      </button>
    </aside>
  </div>
</template>

<script>
import emptyStateIconSvg from '@/static/icons/ultimas-muestras-icon-empty-state.svg';
import inAlertSvgIcon from '@/static/icons/en_alerta.svg';
import inReviewIconSvg from '@/static/icons/sin_revisar.svg';
import inProgresIconSvg from '@/static/icons/en_progreso.svg';
import historyIconSvg from '@/static/icons/ver-historial-ultimas-muestras.svg';

export default {
  name: 'LastSamplesEmptyState',
  props: {
    lastRefreshAt: {
      type: [Date, String, Number],
      default: null,
    },
  },
  computed: {
    emptyStateIconSvg: () => emptyStateIconSvg,
    historyIconSvg: () => historyIconSvg,
    counters() {
      return [
        {
          key: 'alert',
          label: this.$t('lastSamples.inAlert'),
          icon: inAlertSvgIcon,
        },
        {
          key: 'review',
          label: this.$t('lastSamples.notReviewed'),
          icon: inReviewIconSvg,
        },
        {
          key: 'process',
          label: this.$t('lastSamples.inProcess'),
          icon: inProgresIconSvg,
        },
      ];
    },
    formattedLastRefreshAt() {
      const date = this.getValidRefreshDate();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');

      return `${this.$t(
        'lastSamples.emptyState.today'
      )} ${hours}:${minutes}`;
    },
  },
  methods: {
    getValidRefreshDate() {
      const date = this.lastRefreshAt
        ? new Date(this.lastRefreshAt)
        : new Date();

      if (Number.isNaN(date.getTime())) {
        return new Date();
      }

      return date;
    },
    navigateToSamplesHistory() {
      this.$f7router.navigate('/SampleSummary', {
        animate: true,
        transition: 'f7-fade',
      });
    },
  },
};
</script>

<style scoped lang="scss">
.last-samples-empty {
  width: 100%;
  min-height: 178px;
  display: grid;
  grid-template-columns: minmax(170px, 18%) minmax(0, 1fr) minmax(
      210px,
      22%
    );
  align-items: center;
  gap: 18px;
  box-sizing: border-box;
}

.last-samples-empty__illustration {
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    max-width: 185px;
    max-height: 165px;
    display: block;
    object-fit: contain;
  }
}

.last-samples-empty__content {
  min-width: 0;
  justify-self: start;
}

.last-samples-empty__title {
  margin: 0 0 8px;
  color: var(--Primary-Blue-60, #04509f);
  font-size: 17px;
  font-weight: 700;
  line-height: 1.22;
}

.last-samples-empty__description {
  margin: 0;
  color: var(--Neutral-70, #3e3c44);
  font-size: 14px;
  line-height: 1.48;

  & + & {
    margin-top: 4px;
  }

  strong {
    color: var(--Primary-Blue-60, #04509f);
    font-weight: 700;
  }
}

.last-samples-empty__counters {
  width: 100%;
  margin-top: 18px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  box-shadow: 0 1px 4px 0 rgba(87, 84, 95, 0.3);
  border-radius: 8px;
  background: var(--Neutral-10, #ffffff);
}

.last-samples-empty__counter {
  min-width: 0;
  min-height: 62px;
  padding: 9px 10px;
  display: grid;
  grid-template-columns: 30px auto minmax(0, 1fr);
  align-items: center;
  column-gap: 8px;
  box-sizing: border-box;

  & + & {
    border-left: 1px solid var(--Neutral-20, #e8e7ea);
  }
}

.last-samples-empty__counter-icon {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(4, 80, 159, 0.08);

  img {
    width: 18px;
    height: 18px;
    display: block;
    object-fit: contain;
  }
}

.last-samples-empty__counter--alert {
  .last-samples-empty__counter-icon {
    color: var(--Danger-60, #d64545);
    background: rgba(214, 69, 69, 0.08);
  }
}

.last-samples-empty__counter--review {
  .last-samples-empty__counter-icon {
    color: var(--Neutral-70, #3e3c44);
    background-color: var(--Neutral-20, #e5e4e7);
  }
}

.last-samples-empty__counter--process {
  .last-samples-empty__counter-icon {
    color: var(--Primary-Blue-60, #04509f);
    background-color: var(--primary-Blue-20, #c2d8f0);
  }
}

.last-samples-empty__counter-number {
  color: var(--Primary-Blue-70, #00346a);
  font-size: 20px;
  font-weight: 700;
  line-height: 1;
}

.last-samples-empty__counter-label {
  min-width: 0;
  max-width: 100%;
  color: var(--Neutral-70, #3e3c44);
  font-size: 13px;
  font-weight: 600;
  line-height: 1.25;
  overflow-wrap: anywhere;
}

.last-samples-empty__side {
  min-width: 0;
  align-self: stretch;
  padding-left: 24px;
  border-left: 1px solid var(--Neutral-20, #e8e7ea);
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.last-samples-empty__status-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.last-samples-empty__status-icon {
  width: 18px;
  height: 18px;
  flex: 0 0 18px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--Secondary-green-50, #83ad23);
  color: var(--Basic-white, #fff);

  svg {
    width: 12px;
    height: 12px;
    fill: none;
    stroke: currentColor;
    stroke-width: 3.2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
}

.last-samples-empty__history-link {
  width: 100%;
  min-height: 42px;
  margin-top: 14px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--Primary-Blue-70, #00346a);
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr) 18px;
  align-items: center;
  gap: 8px;
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.25;
  cursor: pointer;
  border-radius: 8px;
  transition: transform 0.2s ease, background-color 0.2s ease,
    color 0.2s ease;

  > svg {
    width: 18px;
    height: 18px;
    fill: none;
    stroke: currentColor;
    stroke-width: 2.4;
    stroke-linecap: round;
    stroke-linejoin: round;
    transition: transform 0.2s ease;
  }

  &:hover {
    transform: translateY(-1px);
    color: var(--Primary-Blue-60, #04509f);

    .last-samples-empty__history-arrow {
      transform: translateX(3px);
    }
  }

  &:focus-visible {
    outline: 3px solid rgba(4, 80, 159, 0.18);
    outline-offset: 4px;
    border-radius: 8px;
  }
}

.last-samples-empty__history-icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(4, 80, 159, 0.08);

  i {
    font-size: 17px;
  }

  img {
    width: 17px;
    height: 17px;
    display: block;
    object-fit: contain;
  }
}

@media (min-width: 1537px) {
  .last-samples-empty__status-icon {
    margin-left: 6px;
  }

  .last-samples-empty__counter-label {
    font-size: 14px;
  }

}

@media (max-width: 1440px) {
  .last-samples-empty__status-icon {
     margin-left: 0;
  }
  .last-samples-empty__counters {
    display: none;
  }
  .last-samples-empty__counter-label {
    font-size: 13px;
  }
}

@media (max-width: 1280px) {
  .last-samples-empty {
    grid-template-columns: minmax(160px, 22%) minmax(0, 1fr);
  }

  .last-samples-empty__side {
    grid-column: 2;
    padding: 18px 0 0;
    border-left: 0;
    border-top: 1px solid var(--Neutral-20, #e8e7ea);
  }
}

@media (max-width: 768px) {
  .last-samples-empty {
    grid-template-columns: 1fr;
    justify-items: center;
    text-align: center;
    gap: 18px;
  }

  .last-samples-empty__illustration {
    img {
      max-width: 185px;
      max-height: 165px;
    }
  }

  .last-samples-empty__title {
    font-size: 20px;
  }

  .last-samples-empty__counters {
    grid-template-columns: 1fr;
  }

  .last-samples-empty__counter {
    grid-template-columns: 30px auto 1fr;
    justify-items: start;

    & + & {
      border-left: 0;
      border-top: 1px solid var(--Neutral-20, #e8e7ea);
    }
  }

  .last-samples-empty__side {
    grid-column: auto;
    width: 100%;
    padding-top: 18px;
    align-items: center;
  }

  .last-samples-empty__history-link {
    max-width: 320px;
  }
}
</style>
